import {
  Card,
  CircularProgress,
  Grid2,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { DeleteIcon, UploadBigIcon } from "components/Icons";
import React, { useState } from "react";
import { Show } from "../Show";

interface DragDropProps {
  value: File[];
  onChange: (files: File[]) => void;
  setUploadError: (files: string) => void;
  uploadError: string;
  isLoading?: boolean;
  showSupportedFormat?: boolean;
  supportName?: string;
  className?: string;
  maxHeight?: string;
  acceptType?: string;
  error?: boolean;
  disabled?: boolean;
  onDelete?: any;
  multiple?: boolean;
}

const FileInput = styled("input")(() => {
  return {
    cursor: "pointer",
    zIndex: 99,
    height: "100%",
    top: 0,
    position: "absolute",
    opacity: 0,
    width: "100%",
  };
});

const DragDrop = (props: DragDropProps) => {
  const {
    onChange,
    isLoading = false,
    value = [],
    supportName,
    acceptType,
    multiple,
    disabled,
    error,
    uploadError,
    setUploadError,
    onDelete,
  } = props;

  const [isDragOver, setIsDragOver] = useState(false);

  const acceptedTypes = acceptType
    ?.split(",")
    .map((type) => type.trim().toLowerCase());

  const isValidFileType = (file: File) => {
    if (!acceptedTypes) return true;
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    return (
      acceptedTypes.includes(fileType) ||
      acceptedTypes.includes(`.${fileExtension}`)
    );
  };

  const handleDragEnter = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const filteredFiles = Array.from(event.dataTransfer.files).filter(
        isValidFileType,
      );
      if (filteredFiles.length > 0) {
        onChange(filteredFiles);
        setUploadError(null);
      } else {
        setUploadError("Unsupported file type.");
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.target.files && event.target.files.length > 0) {
      const filteredFiles = Array.from(event.target.files).filter(
        isValidFileType,
      );
      if (filteredFiles.length > 0) {
        onChange(filteredFiles);
        setUploadError(null);
      } else {
        setUploadError("Unsupported file type.");
      }
    }
    event.target.value = "";
  };

  const handleDelete = (index: number) => {
    const newValue = value?.filter((data, i) => i !== index);
    onChange(newValue);
  };

  return (
    <div
      style={{
        width: "99.8%",
        position: "relative",
        overflow: "hidden auto",
      }}
    >
      <Show.When isTrue={value?.length === 0}>
        <label
          htmlFor="file-upload-input"
          style={{
            border: `1px`,
            backgroundColor: disabled ? grey[100] : "var(--white)",
            borderColor:
              error || uploadError
                ? `var(--warning-700)`
                : "var(--neutral-100)",
            borderStyle: error || uploadError ? "solid" : "dashed",
            display: "block",
            borderRadius: "0.75rem",
            height: "0",
            cursor: "pointer",
            padding: "40px 20px",
            ...(isDragOver && {
              backgroundColor: "var(--neutral-500)",
              borderColor: "var(--primary-900)",
            }),
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div
            style={{
              pointerEvents: "none",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              position: "relative",
            }}
          >
            <Grid2 container spacing={2} width={"100%"}>
              <Grid2
                size={"auto"}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: "var(--neutral-50)",
                    borderRadius: 100,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 10,
                  }}
                >
                  <UploadBigIcon height={32} width={32} />
                </div>
              </Grid2>
              <Grid2 size={10} display={"flex"} alignItems={"center"}>
                <Typography
                  fontSize={14}
                  fontWeight={400}
                  color={"var(--black)"}
                >
                  Drag & Drop your file here or
                  <span
                    style={{
                      color: "var(--primary-900)",
                      textDecoration: "underline",
                      marginLeft: 3,
                    }}
                  >
                    Choose Files
                  </span>
                </Typography>
                <Typography
                  fontSize={12}
                  color={"var(--neutral-500)"}
                  fontWeight={400}
                >
                  {"  "} Supported formats :{" "}
                  {supportName || "All files are supported."}
                </Typography>
                {uploadError && (
                  <Typography
                    fontSize={12}
                    color={`var(--warning-700)`}
                    textAlign={"center"}
                  >
                    {uploadError}
                  </Typography>
                )}
              </Grid2>
            </Grid2>
            {isLoading && <CircularProgress />}
          </div>
          <FileInput
            type="file"
            onChange={handleChange}
            accept={acceptType}
            disabled={disabled}
            multiple={multiple}
          />
        </label>
      </Show.When>

      <Show.When isTrue={value?.length !== 0}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            width: "100%",
          }}
        >
          {value?.map((dataItem: any, index: any) => {
            return (
              <Grid2 container key={index}>
                <Grid2 size={12}>
                  <Card
                    sx={{
                      borderColor: "var(--neutral-100)",
                      p: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Grid2 container alignItems={"center"}>
                      <Grid2
                        container
                        alignContent={"center"}
                        gap={1}
                        size={11}
                      >
                        <Grid2 display={"flex"} alignItems={"center"}>
                          <img
                            src={`/FileIcons/${(dataItem?.name || "")
                              .split(".")
                              .pop()}.svg`}
                            height={30}
                            width={30}
                            alt=""
                            style={{
                              borderRadius: 4,
                              border: "1px solid",
                              borderColor: "var(--neutral-100)",
                              padding: 4,
                            }}
                          />
                        </Grid2>
                        <Grid2>
                          <Typography fontSize={12} fontWeight={600}>
                            {dataItem?.name}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 size={1} textAlign={"right"}>
                        <IconButton
                          className="outline"
                          color="error"
                          onClick={() => {
                            if (onDelete) {
                              onDelete();
                            } else {
                              handleDelete(index);
                            }
                          }}
                        >
                          <DeleteIcon width={18} height={18} />
                        </IconButton>
                      </Grid2>
                    </Grid2>
                  </Card>
                </Grid2>
              </Grid2>
            );
          })}
        </div>
      </Show.When>
    </div>
  );
};

export default DragDrop;
